import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiFetch } from "@/lib/api";

interface CodeSegment {
  type: string;
  value?: string;
  source?: string;
  format?: string;
  digits?: number;
  reset_period?: string;
}

interface CodeRuleRow {
  codeType: string;
  description: string;
  segments: CodeSegment[];
  maxTotalLength: number;
  version: number;
  isActive: boolean;
  updatedAt: string;
}

interface CodeSequenceRow {
  scope: string;
  nextValue: number;
  updatedAt: string;
}

const SEGMENT_LABELS: Record<string, string> = {
  literal: "固定字符",
  date: "日期",
  sequence: "流水号",
  ref: "引用",
};

export function SettingsCodeRulesPage() {
  const rules = useQuery({
    queryKey: ["code-rules"],
    queryFn: () => apiFetch<CodeRuleRow[]>("/api/v1/admin/code-rules"),
  });
  const sequences = useQuery({
    queryKey: ["code-sequences"],
    queryFn: () => apiFetch<CodeSequenceRow[]>("/api/v1/admin/code-sequences"),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="编号规则"
        description="查看物料、资产、许可、批次、申请等编号段类型与下一流水号。"
      />

      <Alert variant="info" title="当前为只读展示">
        编号生成器按 SOP
        固定段规则运行；为避免破坏既有编号连续性，本页不提供在线改段。需要调整时先评估历史数据与唯一性。
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>规则定义</CardTitle>
          <CardDescription>共 {rules.data?.length ?? 0} 类编号。</CardDescription>
        </CardHeader>
        <CardContent>
          {rules.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>类型</TableHead>
                  <TableHead>说明</TableHead>
                  <TableHead>段结构</TableHead>
                  <TableHead>最大长度</TableHead>
                  <TableHead>版本</TableHead>
                  <TableHead>状态</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rules.data ?? []).map((rule) => (
                  <TableRow key={rule.codeType}>
                    <TableCell className="font-mono text-xs">{rule.codeType}</TableCell>
                    <TableCell>{rule.description}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {rule.segments.map((segment, index) => (
                          <Badge
                            key={`${rule.codeType}-${segment.type}-${segment.value ?? segment.source ?? segment.format ?? segment.digits ?? index}`}
                            variant="secondary"
                          >
                            {SEGMENT_LABELS[segment.type] ?? segment.type}
                            {segment.value ? `:${segment.value}` : ""}
                            {segment.source ? `:${segment.source}` : ""}
                            {segment.format ? `:${segment.format}` : ""}
                            {segment.digits ? `:${segment.digits}` : ""}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{rule.maxTotalLength}</TableCell>
                    <TableCell>v{rule.version}</TableCell>
                    <TableCell>
                      <Badge variant={rule.isActive ? "success" : "secondary"}>
                        {rule.isActive ? "启用" : "停用"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>下一流水号</CardTitle>
          <CardDescription>按 scope 记录；编号生成时原子递增。</CardDescription>
        </CardHeader>
        <CardContent>
          {sequences.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (sequences.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">尚未产生流水号。</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Scope</TableHead>
                  <TableHead>下一值</TableHead>
                  <TableHead>更新时间</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(sequences.data ?? []).map((row) => (
                  <TableRow key={row.scope}>
                    <TableCell className="font-mono text-xs">{row.scope}</TableCell>
                    <TableCell>{row.nextValue}</TableCell>
                    <TableCell>{row.updatedAt}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
